import React, { useState } from 'react';

interface MonthlyReportFormProps {
  projectId: string;
  projectTitle: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function MonthlyReportForm({ projectId, projectTitle, onSuccess, onCancel }: MonthlyReportFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ragStatus, setRagStatus] = useState<'Green' | 'Amber' | 'Red'>('Green');
  const [confidence, setConfidence] = useState<'High' | 'Medium' | 'Low'>('High');
  const [progressNotes, setProgressNotes] = useState('');
  const [hoursLogged, setHoursLogged] = useState(0);

  const currentMonth = new Date().toISOString().slice(0, 7);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/reports/monthly', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          monthYear: currentMonth,
          ragStatus,
          confidence,
          progressNotes: progressNotes || undefined,
          actualHoursLogged: hoursLogged,
          submittedByEmail: 'test@example.com',
          submittedByName: 'Test User',
        }),
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        setError(data.error || 'Failed to submit report');
      }
    } catch (err: any) {
      setError(err.message || 'Error submitting report');
    } finally {
      setLoading(false);
    }
  };

  const ragColors = {
    Green: '#28a745',
    Amber: '#ffc107',
    Red: '#dc3545',
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Monthly Report: {projectTitle}</h2>
          <button onClick={onCancel} className="btn-close">×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Month:</label>
              <input type="text" value={currentMonth} disabled className="form-control" />
            </div>

            <div className="form-group">
              <label>RAG Status:</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                {(['Green', 'Amber', 'Red'] as const).map(status => (
                  <label key={status} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="ragStatus"
                      value={status}
                      checked={ragStatus === status}
                      onChange={(e) => setRagStatus(e.target.value as 'Green' | 'Amber' | 'Red')}
                    />
                    <span style={{ color: ragColors[status], fontWeight: 600 }}>{status}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Confidence Level:</label>
              <select value={confidence} onChange={(e) => setConfidence(e.target.value as 'High' | 'Medium' | 'Low')}>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div className="form-group">
              <label>Progress Notes:</label>
              <textarea
                value={progressNotes}
                onChange={(e) => setProgressNotes(e.target.value)}
                placeholder="What happened this month? Any blockers?"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Actual Hours Logged:</label>
              <input
                type="number"
                value={hoursLogged}
                onChange={(e) => setHoursLogged(parseFloat(e.target.value) || 0)}
                placeholder="0"
                step="0.5"
                min="0"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Report'}
              </button>
              <button type="button" className="btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
